import { HTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export type CardVariant = 'default' | 'bordered' | 'elevated';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
}

const CardRoot = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'card',
          variant === 'bordered' && 'border',
          variant === 'elevated' && 'shadow-lg',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardRoot.displayName = 'Card';

// Card sub-components
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, action, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'px-5 py-4',
          className
        )}
        style={{ borderBottom: '1px solid var(--color-border)' }}
        {...props}
      >
        {(title || subtitle || action) ? (
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {title && (
                <h3
                  className="text-heading-base"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {title}
                </h3>
              )}
              {subtitle && (
                <p
                  className="mt-1 text-sm"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {subtitle}
                </p>
              )}
            </div>
            {action && (
              <div className="ml-4">
                {action}
              </div>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ noPadding = false, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          !noPadding && 'p-5',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'px-5 py-4 rounded-b-card',
          className
        )}
        style={{
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-bg-sunken)',
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// CardTitle sub-component
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {}

const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={clsx(
          'text-heading-base',
          className
        )}
        style={{ color: 'var(--color-text-primary)' }}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

// Export Card with sub-components
export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
  Title: CardTitle,
});
